import { z } from 'zod';
import {
  isOpenWorldCoordinateWithin,
  OPEN_WORLD_ACTIVE_BOUNDS,
  type OpenWorldBounds,
  type OpenWorldCoordinate,
  openWorldCoordinate,
} from './grid';

/**
 * The four directions, and what a neighbour is.
 *
 * ## Four, and never eight
 *
 * `north`, `south`, `east`, `west`. There is no diagonal, and this is not an
 * omission to be filled in later: a diagonal crossing would have to decide
 * which of two edges a ship left through, which corner it arrived at, and what
 * happens when one of the two intermediate quadrants refuses entry. Four
 * directions make a crossing a single unambiguous event, and every later
 * mechanic - travel, pursuit, blockades - inherits that.
 *
 * ## North is up, and up is a smaller row index
 *
 * Row `A` is the northern edge, so moving north DECREASES `rowIndex`. Moving
 * east increases `columnIndex`, towards column `10`. Stated once here, so
 * nothing downstream has to re-derive the sign of an axis:
 *
 * - `C/5` north is `B/5`; `C/5` south is `D/5`.
 * - `F/10` west is `F/9`; `F/10` east does not exist in this world.
 *
 * ## Off the edge is `null`, not an error
 *
 * A quadrant on the boundary genuinely has fewer neighbours, and that is an
 * ordinary fact a traversal has to handle. It is distinct from a MALFORMED
 * coordinate, which {@link openWorldCoordinate} refuses to build at all - so a
 * `null` here always means "the world ends", never "the caller passed rubbish".
 *
 * Because the bounds are an argument, widening the world to 26x26 turns some of
 * those `null`s into quadrants without a line of this file changing.
 */

export const OPEN_WORLD_DIRECTIONS = ['north', 'south', 'east', 'west'] as const;

export type OpenWorldDirection = (typeof OPEN_WORLD_DIRECTIONS)[number];

export const openWorldDirectionSchema = z.enum(OPEN_WORLD_DIRECTIONS);

export function isOpenWorldDirection(value: string): value is OpenWorldDirection {
  return (OPEN_WORLD_DIRECTIONS as readonly string[]).includes(value);
}

/** How each direction moves the two indices. The one place the signs live. */
export const OPEN_WORLD_DIRECTION_STEPS: Readonly<
  Record<OpenWorldDirection, { readonly rowStep: number; readonly columnStep: number }>
> = Object.freeze({
  north: Object.freeze({ rowStep: -1, columnStep: 0 }),
  south: Object.freeze({ rowStep: 1, columnStep: 0 }),
  east: Object.freeze({ rowStep: 0, columnStep: 1 }),
  west: Object.freeze({ rowStep: 0, columnStep: -1 }),
});

const OPPOSITES: Readonly<Record<OpenWorldDirection, OpenWorldDirection>> = Object.freeze({
  north: 'south',
  south: 'north',
  east: 'west',
  west: 'east',
});

/**
 * The way back.
 *
 * A crossing is symmetric by construction: if `C/5` reaches `B/5` going north,
 * `B/5` reaches `C/5` going south. Deriving the return direction rather than
 * storing it is what keeps that true when the bounds change.
 */
export function oppositeOpenWorldDirection(direction: OpenWorldDirection): OpenWorldDirection {
  return OPPOSITES[direction];
}

/**
 * The quadrant one step away, or `null` at the edge of the world.
 *
 * The single traversal primitive. Nothing else in the codebase may add or
 * subtract from a coordinate.
 */
export function openWorldNeighbour(
  coordinate: OpenWorldCoordinate,
  direction: OpenWorldDirection,
  bounds: OpenWorldBounds = OPEN_WORLD_ACTIVE_BOUNDS,
): OpenWorldCoordinate | null {
  const step = OPEN_WORLD_DIRECTION_STEPS[direction];
  const candidate = {
    rowIndex: coordinate.rowIndex + step.rowStep,
    columnIndex: coordinate.columnIndex + step.columnStep,
  };
  if (!isOpenWorldCoordinateWithin(candidate, bounds)) {
    return null;
  }
  return openWorldCoordinate(candidate.rowIndex, candidate.columnIndex, bounds);
}

/** Which directions actually lead somewhere from here. Row-major stable order. */
export function openWorldExits(
  coordinate: OpenWorldCoordinate,
  bounds: OpenWorldBounds = OPEN_WORLD_ACTIVE_BOUNDS,
): readonly OpenWorldDirection[] {
  return Object.freeze(
    OPEN_WORLD_DIRECTIONS.filter(
      (direction) => openWorldNeighbour(coordinate, direction, bounds) !== null,
    ),
  );
}
